import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import LogDisciplineModal from '../discipline/LogDisciplineModal';
import FilterDropdown from '../common/FilterDropdown';
import PremiumTooltip from '../common/PremiumTooltip';
import {
    TrophyIcon,
    ExclamationTriangleIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    ScaleIcon,
    CalendarIcon,
    UserCircleIcon,
    FunnelIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

const TeacherCourseDisciplineTab = ({ courseId, course }) => {
    const { get } = useApi();

    // Modal & Filter states
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('merits'); // 'merits' | 'demerits'
    const [showFilters, setShowFilters] = useState(false);
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');
    const [filterSeverity, setFilterSeverity] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Safe date formatting helper
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '—';
            return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return '—';
        }
    };

    // Fetch Academic Years for Year Filter
    const { data: yearsData = [] } = useQuery({
        queryKey: ['academic-years'],
        queryFn: () => get('/api/academic-years/'),
        staleTime: 60 * 60 * 1000,
    });
    const years = useMemo(() => Array.isArray(yearsData) ? yearsData : (yearsData?.results || []), [yearsData]);

    // Fetch Academic Terms for Term Filter
    const { data: termsData = [] } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 60 * 60 * 1000,
    });
    const terms = useMemo(() => Array.isArray(termsData) ? termsData : (termsData?.results || []), [termsData]);

    const hasInitializedTerm = React.useRef(false);
    React.useEffect(() => {
        if (!hasInitializedTerm.current && terms.length > 0) {
            const current = terms.find(t => t.is_current);
            if (current) {
                setSelectedTerm(String(current.id));
                if (current.year) {
                    setSelectedYear(String(current.year));
                }
            }
            hasInitializedTerm.current = true;
        }
    }, [terms]);

    const yearOptions = useMemo(() => [
        { name: 'All Academic Years', value: '' },
        ...years.map(y => ({
            name: `${y.name} ${y.is_current ? '(Current Year)' : ''}`.trim(),
            value: String(y.id)
        }))
    ], [years]);

    const termOptions = useMemo(() => {
        const filteredTerms = selectedYear
            ? terms.filter(t => String(t.year) === String(selectedYear))
            : terms;
        return [
            { name: 'All Terms', value: '' },
            ...filteredTerms.map(t => ({
                name: `${t.name} ${t.year_name ? `(${t.year_name})` : ''} ${t.is_current ? '• Current' : ''}`.trim(),
                value: String(t.id)
            }))
        ];
    }, [terms, selectedYear]);

    // Fetch Discipline Records scoped to this course and selected term/year
    const {
        data: records = [],
        isLoading,
        isFetching,
        refetch
    } = useQuery({
        queryKey: ['course-discipline-records', courseId, selectedYear, selectedTerm],
        queryFn: async () => {
            let url = `/api/discipline/records/?course_id=${courseId}`;
            if (selectedYear) url += `&year_id=${selectedYear}`;
            if (selectedTerm) url += `&term_id=${selectedTerm}`;
            const res = await get(url);
            return Array.isArray(res) ? res : [];
        },
        enabled: !!courseId,
        staleTime: 30 * 1000,
    });

    // Filtered Records split by Tab
    const meritRecords = useMemo(() => {
        return records.filter(r => r.category_type === 'merit');
    }, [records]);

    const demeritRecords = useMemo(() => {
        return records.filter(r => r.category_type === 'demerit');
    }, [records]);

    const filteredMerits = useMemo(() => {
        return meritRecords.filter(r => {
            if (searchTerm) {
                const q = searchTerm.toLowerCase();
                const matchesStudent = r.student_name?.toLowerCase().includes(q) || (r.student_id_number || r.student_adm)?.toLowerCase().includes(q);
                const matchesTitle = r.title?.toLowerCase().includes(q);
                const matchesCategory = r.category_name?.toLowerCase().includes(q);
                const matchesNotes = r.description?.toLowerCase().includes(q);
                if (!matchesStudent && !matchesTitle && !matchesCategory && !matchesNotes) return false;
            }
            return true;
        });
    }, [meritRecords, searchTerm]);

    const filteredDemerits = useMemo(() => {
        return demeritRecords.filter(r => {
            if (filterSeverity !== 'all' && r.severity !== filterSeverity) return false;
            if (filterStatus !== 'all' && r.status !== filterStatus) return false;
            if (searchTerm) {
                const q = searchTerm.toLowerCase();
                const matchesStudent = r.student_name?.toLowerCase().includes(q) || (r.student_id_number || r.student_adm)?.toLowerCase().includes(q);
                const matchesCategory = r.category_name?.toLowerCase().includes(q);
                const matchesOffense = r.description?.toLowerCase().includes(q);
                const matchesConsequences = r.consequences?.toLowerCase().includes(q);
                if (!matchesStudent && !matchesCategory && !matchesOffense && !matchesConsequences) return false;
            }
            return true;
        });
    }, [demeritRecords, filterSeverity, filterStatus, searchTerm]);

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Header Card Matching Lesson Records Style (Screenshot 3 & 4) */}
            <div className="flex flex-col md:flex-row md:items-center justify-between bg-white rounded-[1.5rem] px-6 py-5 border border-slate-100 shadow-sm gap-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-indigo-50 rounded-xl text-[#18216D]">
                            <ScaleIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight leading-tight">
                                Character Registry
                            </h2>
                            <p className="text-slate-500 font-medium text-xs mt-1">
                                Logged character merits and infractions strictly for enrolled scholars in {course?.name || 'this class'}.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:items-end gap-2.5 w-full md:w-auto">
                    {/* Header Action Buttons: 2 buttons side-by-side */}
                    <div className="flex items-center gap-2.5 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 border transition-all active:scale-95 ${
                                showFilters || selectedYear !== '' || selectedTerm !== '' || (activeTab === 'demerits' && (filterSeverity !== 'all' || filterStatus !== 'all'))
                                    ? 'bg-[#18216D] text-white border-[#18216D] shadow-sm'
                                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <FunnelIcon className="w-3.5 h-3.5" />
                            <span>Filters</span>
                            {(selectedYear !== '' || selectedTerm !== '' || (activeTab === 'demerits' && (filterSeverity !== 'all' || filterStatus !== 'all'))) && (
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                            )}
                        </button>

                        <button
                            onClick={() => setIsLogModalOpen(true)}
                            className="flex-1 sm:flex-initial px-5 py-2.5 bg-[#18216D] hover:bg-indigo-900 text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm transition-all whitespace-nowrap active:scale-95"
                        >
                            <PlusIcon className="w-4 h-4 stroke-[3]" />
                            <span>Log Merit/Demerit</span>
                        </button>
                    </div>

                    {/* Search Input Positioned Underneath the 2 Buttons */}
                    <div className="relative group w-full sm:w-72">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                            <MagnifyingGlassIcon className="w-4 h-4" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search scholar or notes..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-2 bg-slate-50 hover:bg-white border border-slate-200/80 rounded-xl shadow-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D] placeholder:text-slate-400"
                        />
                    </div>
                </div>
            </div>

            {/* Centered Underline Tabs for Merits & Demerits with Refresh Icon on Far Right */}
            <div className="relative border-b border-gray-200 flex items-center justify-center pb-0">
                <nav className="flex space-x-12 justify-center" aria-label="Course Character Tabs">
                    <button
                        type="button"
                        onClick={() => setActiveTab('merits')}
                        className={`pb-3.5 px-4 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${
                            activeTab === 'merits'
                                ? 'text-[#18216D] border-[#18216D]'
                                : 'text-slate-400 hover:text-slate-600 border-transparent'
                        }`}
                    >
                        <TrophyIcon className={`w-4 h-4 ${activeTab === 'merits' ? 'text-amber-500' : 'text-slate-400'}`} />
                        <span>Merits ({filteredMerits.length})</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('demerits')}
                        className={`pb-3.5 px-4 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${
                            activeTab === 'demerits'
                                ? 'text-[#18216D] border-[#18216D]'
                                : 'text-slate-400 hover:text-slate-600 border-transparent'
                        }`}
                    >
                        <ExclamationTriangleIcon className={`w-4 h-4 ${activeTab === 'demerits' ? 'text-rose-500' : 'text-slate-400'}`} />
                        <span>Demerits ({filteredDemerits.length})</span>
                    </button>
                </nav>

                {/* Refresh Icon aligned to the far right of the tabs bar */}
                <div className="absolute right-0 top-0 flex items-center pb-2">
                    <button
                        type="button"
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="p-2 bg-white hover:bg-slate-50 text-[#18216D] rounded-xl border border-slate-200 transition-all active:scale-95 shadow-sm"
                        title="Refresh Records"
                    >
                        <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Pop-Out Filters Section Below Tabs & Above Tables (Matching Character Registry Design) */}
            {showFilters && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-4 animate-fadeIn">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                        <div className="flex items-center gap-2">
                            <FunnelIcon className="w-4 h-4 text-[#18216D]" />
                            <span className="text-xs font-black text-[#18216D] uppercase tracking-wider">
                                Filter Options ({activeTab === 'merits' ? 'Merits' : 'Demerits'})
                            </span>
                            {(selectedYear !== '' || selectedTerm !== '' || (activeTab === 'demerits' && (filterSeverity !== 'all' || filterStatus !== 'all'))) && (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-wider">
                                    Filtered
                                </span>
                            )}
                        </div>
                        {(selectedYear !== '' || selectedTerm !== '' || filterSeverity !== 'all' || filterStatus !== 'all') && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedYear('');
                                    setSelectedTerm('');
                                    setFilterSeverity('all');
                                    setFilterStatus('all');
                                }}
                                className="text-[11px] font-bold text-slate-400 hover:text-rose-600 transition-colors uppercase tracking-wider"
                            >
                                Reset Filters
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Academic Year
                            </label>
                            <FilterDropdown
                                value={selectedYear}
                                onChange={(val) => {
                                    setSelectedYear(val || '');
                                    setSelectedTerm('');
                                }}
                                options={yearOptions}
                                placeholder="All Academic Years"
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Academic Term
                            </label>
                            <FilterDropdown
                                value={selectedTerm}
                                onChange={(val) => setSelectedTerm(val || '')}
                                options={termOptions}
                                placeholder="All Terms"
                                className="w-full"
                            />
                        </div>

                        {activeTab === 'demerits' && (
                            <>
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                        Severity
                                    </label>
                                    <FilterDropdown
                                        value={filterSeverity}
                                        onChange={(val) => setFilterSeverity(val || 'all')}
                                        options={[
                                            { name: 'All Severities', value: 'all' },
                                            { name: 'Minor', value: 'minor' },
                                            { name: 'Moderate', value: 'moderate' },
                                            { name: 'Major', value: 'major' },
                                            { name: 'Severe', value: 'severe' },
                                        ]}
                                        placeholder="All Severities"
                                        className="w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                        Governance Status
                                    </label>
                                    <FilterDropdown
                                        value={filterStatus}
                                        onChange={(val) => setFilterStatus(val || 'all')}
                                        options={[
                                            { name: 'All Statuses', value: 'all' },
                                            { name: 'Logged', value: 'logged' },
                                            { name: 'Pending Review', value: 'pending_review' },
                                            { name: 'Under Action', value: 'under_action' },
                                            { name: 'Resolved', value: 'resolved' },
                                            { name: 'Escalated', value: 'escalated' },
                                        ]}
                                        placeholder="All Statuses"
                                        className="w-full"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Merits Table View */}
            {activeTab === 'merits' && (
                <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden animate-fadeIn">
                    {isLoading ? (
                        <div className="py-20 text-center space-y-3">
                            <div className="w-10 h-10 border-4 border-[#18216D] border-t-transparent rounded-full animate-spin mx-auto" />
                            <p className="text-xs font-black uppercase tracking-widest text-slate-400">Loading Merits...</p>
                        </div>
                    ) : filteredMerits.length === 0 ? (
                        <div className="py-20 text-center">
                            <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-4">
                                <TrophyIcon className="w-8 h-8" />
                            </div>
                            <h4 className="text-base font-black text-slate-800 uppercase tracking-tight">No Merits Recorded</h4>
                            <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                {searchTerm
                                    ? 'No merit records match your current search criteria.'
                                    : 'No commendations logged for this class yet. Click below to recognize outstanding scholar behavior.'}
                            </p>
                            <button
                                onClick={() => setIsLogModalOpen(true)}
                                className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#18216D] text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-[#0D164F] transition-all shadow-md active:scale-95"
                            >
                                <PlusIcon className="w-4 h-4 stroke-[3]" />
                                <span>Log Merit for this Class</span>
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/75 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        <th className="py-4 px-6">Scholar</th>
                                        <th className="py-4 px-6">Category</th>
                                        <th className="py-4 px-6 text-center">Points</th>
                                        <th className="py-4 px-6">Commendation Notes</th>
                                        <th className="py-4 px-6">Date</th>
                                        <th className="py-4 px-6 text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-xs">
                                    {filteredMerits.map(record => (
                                        <tr key={record.id} className="transition-colors hover:bg-emerald-50/30">
                                            {/* Scholar info */}
                                            <td className="py-4 px-6">
                                                <div>
                                                    <p className="font-black text-[#18216D] text-xs sm:text-sm">
                                                        {record.student_name}
                                                    </p>
                                                    <p className="text-[10px] font-bold text-slate-400">
                                                        Adm: {record.student_id_number || record.student_adm || 'N/A'}
                                                    </p>
                                                </div>
                                            </td>

                                            {/* Category (No badge!) */}
                                            <td className="py-4 px-6">
                                                <div className="space-y-0.5">
                                                    {record.title && (
                                                        <p className="font-black text-[#18216D] text-xs">
                                                            {record.title}
                                                        </p>
                                                    )}
                                                    <p className={record.title ? "font-medium text-slate-600 text-xs" : "font-black text-slate-800 text-xs"}>
                                                        {record.category_name}
                                                    </p>
                                                </div>
                                            </td>

                                            {/* Points */}
                                            <td className="py-4 px-6 text-center">
                                                <span className="inline-block px-3 py-1 rounded-xl text-xs font-black bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                    +{record.points} pts
                                                </span>
                                            </td>

                                            {/* Commendation Notes */}
                                            <td className="py-4 px-6 max-w-xs">
                                                {record.description ? (
                                                    <PremiumTooltip
                                                        text={record.description}
                                                        position="top"
                                                        allowWrap={true}
                                                        maxWidth="max-w-xs sm:max-w-sm"
                                                        wrapperClassName="relative block w-full"
                                                        textClassName="text-xs font-medium text-slate-700 normal-case tracking-normal"
                                                    >
                                                        <p className="text-slate-700 font-medium truncate text-xs cursor-pointer hover:text-[#18216D]">
                                                            {record.description}
                                                        </p>
                                                    </PremiumTooltip>
                                                ) : (
                                                    <span className="text-slate-300 text-xs italic">No commendation notes</span>
                                                )}
                                            </td>

                                            {/* Standalone Date Column */}
                                            <td className="py-4 px-6 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5 text-xs text-slate-600 font-bold">
                                                    <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                                                    <span>{formatDate(record.logged_at || record.created_at)}</span>
                                                </div>
                                            </td>

                                            {/* Status: A merit can only be Logged */}
                                            <td className="py-4 px-6 text-center">
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                    Logged
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Demerits Table View */}
            {activeTab === 'demerits' && (
                <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden animate-fadeIn">
                    {isLoading ? (
                        <div className="py-20 text-center space-y-3">
                            <div className="w-10 h-10 border-4 border-[#18216D] border-t-transparent rounded-full animate-spin mx-auto" />
                            <p className="text-xs font-black uppercase tracking-widest text-slate-400">Loading Demerits...</p>
                        </div>
                    ) : filteredDemerits.length === 0 ? (
                        <div className="py-20 text-center">
                            <div className="w-16 h-16 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4">
                                <ExclamationTriangleIcon className="w-8 h-8" />
                            </div>
                            <h4 className="text-base font-black text-slate-800 uppercase tracking-tight">No Demerits Recorded</h4>
                            <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                {searchTerm
                                    ? 'No demerit records match your current filter criteria.'
                                    : 'No infractions logged for this class yet.'}
                            </p>
                            <button
                                onClick={() => setIsLogModalOpen(true)}
                                className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#18216D] text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-[#0D164F] transition-all shadow-md active:scale-95"
                            >
                                <PlusIcon className="w-4 h-4 stroke-[3]" />
                                <span>Log Demerit for this Class</span>
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/75 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        <th className="py-4 px-6">Scholar</th>
                                        <th className="py-4 px-6">Category</th>
                                        <th className="py-4 px-6 text-center">Severity</th>
                                        <th className="py-4 px-6 text-center">Points</th>
                                        <th className="py-4 px-6">Offense Details</th>
                                        <th className="py-4 px-6">Consequences</th>
                                        <th className="py-4 px-6">Date</th>
                                        <th className="py-4 px-6">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-xs">
                                    {filteredDemerits.map(record => (
                                        <tr key={record.id} className="transition-colors hover:bg-rose-50/20">
                                            {/* Scholar info */}
                                            <td className="py-4 px-6">
                                                <div>
                                                    <p className="font-black text-[#18216D] text-xs sm:text-sm">
                                                        {record.student_name}
                                                    </p>
                                                    <p className="text-[10px] font-bold text-slate-400">
                                                        Adm: {record.student_id_number || record.student_adm || 'N/A'}
                                                    </p>
                                                </div>
                                            </td>

                                                {/* Category (No badge!) */}
                                                <td className="py-4 px-6">
                                                    <p className="font-black text-slate-800 text-xs">
                                                        {record.category_name}
                                                    </p>
                                                </td>

                                                {/* Severity */}
                                                <td className="py-4 px-6 text-center">
                                                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                                                        record.severity === 'severe'
                                                            ? 'bg-rose-600 text-white'
                                                            : record.severity === 'major'
                                                            ? 'bg-orange-100 text-orange-800'
                                                            : record.severity === 'moderate'
                                                            ? 'bg-amber-100 text-amber-800'
                                                            : 'bg-slate-100 text-slate-700'
                                                    }`}>
                                                        {record.severity}
                                                    </span>
                                                </td>

                                                {/* Points */}
                                                <td className="py-4 px-6 text-center">
                                                    <span className="inline-block px-3 py-1 rounded-xl text-xs font-black bg-rose-50 text-rose-700 border border-rose-200">
                                                        -{record.points} pts
                                                    </span>
                                                </td>

                                                {/* Offense Details */}
                                                <td className="py-4 px-6 max-w-xs">
                                                    {record.description ? (
                                                        <PremiumTooltip
                                                            text={record.description}
                                                            position="top"
                                                            allowWrap={true}
                                                            maxWidth="max-w-xs sm:max-w-sm"
                                                            wrapperClassName="relative block w-full"
                                                            textClassName="text-xs font-medium text-slate-700 normal-case tracking-normal"
                                                        >
                                                            <p className="text-slate-700 font-medium truncate text-xs cursor-pointer hover:text-[#18216D]">
                                                                {record.description}
                                                            </p>
                                                        </PremiumTooltip>
                                                    ) : (
                                                        <span className="text-slate-300 text-xs italic">No description</span>
                                                    )}
                                                </td>

                                                {/* Consequences */}
                                                <td className="py-4 px-6 max-w-xs">
                                                    {record.consequences ? (
                                                        <PremiumTooltip
                                                            text={record.consequences}
                                                            position="top"
                                                            allowWrap={true}
                                                            maxWidth="max-w-xs sm:max-w-sm"
                                                            wrapperClassName="relative block w-full"
                                                            textClassName="text-xs font-medium text-slate-700 normal-case tracking-normal"
                                                        >
                                                            <p className="text-slate-700 font-medium truncate text-xs cursor-pointer hover:text-[#18216D]">
                                                                {record.consequences}
                                                            </p>
                                                        </PremiumTooltip>
                                                    ) : (
                                                        <span className="text-slate-300 text-xs italic">—</span>
                                                    )}
                                                </td>

                                                {/* Standalone Date Column */}
                                                <td className="py-4 px-6 whitespace-nowrap">
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-600 font-bold">
                                                        <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                                                        <span>{formatDate(record.logged_at || record.created_at)}</span>
                                                    </div>
                                                </td>

                                                {/* Status & Owner */}
                                                <td className="py-4 px-6">
                                                    <div className="space-y-1">
                                                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                                                            record.status === 'resolved'
                                                                ? 'bg-emerald-100 text-emerald-800'
                                                                : record.status === 'under_action'
                                                                ? 'bg-blue-100 text-blue-800'
                                                                : record.status === 'escalated'
                                                                ? 'bg-purple-100 text-purple-800'
                                                                : record.status === 'voided'
                                                                ? 'bg-slate-200 text-slate-500 line-through'
                                                                : 'bg-slate-100 text-slate-700'
                                                        }`}>
                                                            {record.status?.replace('_', ' ')}
                                                        </span>

                                                        {record.active_owners_detail && record.active_owners_detail.length > 0 && (
                                                            <div className="text-[10px] text-slate-500 font-bold flex items-center gap-1">
                                                                <UserCircleIcon className="w-3.5 h-3.5 text-slate-400" />
                                                                <span>{record.active_owners_detail.map(o => o.name).join(', ')}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Reusable Log Discipline Modal */}
            <LogDisciplineModal
                isOpen={isLogModalOpen}
                onClose={() => setIsLogModalOpen(false)}
                onSuccess={() => refetch()}
                preselectedCourseId={courseId}
                customStudentList={course?.enrolled_students || []}
            />
        </div>
    );
};

export default TeacherCourseDisciplineTab;
